import type { Brand } from '@seek/melways-sites';
import React, { useState } from 'react';

import * as constants from '../../../constants';
import { Flow } from '../../../modules/Auth0/types';
import { AnalyticsProvider } from '../../../modules/analytics/AnalyticsContext';
import type { AuthenticationActionOrigin } from '../../../modules/analytics/types';
import {
  type ApplyContext,
  type EmailLookupResult,
  type Environment,
  Experience,
} from '../../../types';
import { load } from '../../../utils/local-storage';
import validateLoginMessage from '../../../utils/validateLoginMessage';
import isApplyNudge from '../../shared/Nudge/utils/isApplyNudge';
import { RecaptchaProvider } from '../../shared/ReCaptcha/RecaptchaContext';

import type { Message as LoginMessage } from './LoginForm/LoginMessage/types';
import getSearchParameters from './getSearchParameters';
import useApplyContext from './shared/hooks/useApplyContext';
import useDebouncedLookup from './shared/hooks/useDebouncedLookup';

interface State {
  emailLookupResult?: EmailLookupResult;
  loginMessage: LoginMessage | null;
  nudge: string;
  emailAddress: string;
  applyContext: ApplyContext | null;
  setEmailAddress: React.Dispatch<React.SetStateAction<string>>;
  adobe_mc: string;
  realmHint: string | undefined;
  realm: string | undefined;
  origin: AuthenticationActionOrigin | undefined;
  flow: Flow;
  setFlow: React.Dispatch<React.SetStateAction<Flow>>;
}

interface ProviderProps {
  children: React.ReactNode;
  experience: Experience;
  environment: Environment;
  isMobileClient?: boolean;
  locale: string;
  brand?: Brand;
}

interface TestProviderProps
  extends Omit<
    ProviderProps,
    'experience' | 'environment' | 'locale' | 'brand'
  > {
  overrides?: Partial<State>;
}

const defaultValue = {
  loginMessage: null,
  environment: 'development',
  nudge: '',
  emailAddress: '',
  applyContext: null,
  setEmailAddress: () => {},
  adobe_mc: '',
  realmHint: undefined,
  realm: undefined,
  origin: undefined,
  flow: Flow.Login,
  setFlow: () => {},
};
const LoginPageContext = React.createContext<State | undefined>(defaultValue);

export const useLoginPageContext = () => {
  const context = React.useContext(LoginPageContext);

  if (!context) {
    throw new Error('loginPageState must be used within a LoginPageProvider');
  }

  return context;
};

export const LoginPageProvider = ({
  environment,
  experience,
  isMobileClient,
  children,
  locale,
  brand,
}: ProviderProps) => {
  const { loginMessage, emailHint, nudge, adobe_mc, realm } =
    getSearchParameters();

  const realmOverride = isMobileClient
    ? 'Username-Password-Authentication'
    : realm; // for mobile client switch the realm for testing provision flow.

  const realmHint = load(constants.localStorage.CONNECTION);

  const { emailAddress, setEmailAddress, emailLookupResult } =
    useDebouncedLookup(environment, experience, emailHint);
  const applyContext = useApplyContext(environment, nudge, locale, brand);

  const origin: AuthenticationActionOrigin =
    (isApplyNudge(nudge) && 'apply') ||
    (nudge === 'shareableProfile' ? 'profile' : 'direct');

  const [flow, setFlow] = useState(Flow.Login);

  return (
    <LoginPageContext.Provider
      value={{
        applyContext,
        emailAddress,
        emailLookupResult,
        loginMessage: validateLoginMessage(loginMessage),
        nudge: nudge || '',
        setEmailAddress,
        adobe_mc,
        realmHint,
        realm: realmOverride,
        origin,
        flow,
        setFlow,
      }}
    >
      <RecaptchaProvider>
        <AnalyticsProvider experience={experience} actionOrigin={origin}>
          {children}
        </AnalyticsProvider>
      </RecaptchaProvider>
    </LoginPageContext.Provider>
  );
};

export const LoginPageTestProvider = ({
  overrides = {},
  children,
}: TestProviderProps) => (
  <LoginPageContext.Provider
    value={{
      ...defaultValue,
      ...overrides,
      setEmailAddress: () => {},
    }}
  >
    <RecaptchaProvider>
      <AnalyticsProvider
        actionOrigin="direct"
        experience={Experience.Candidate}
      >
        {children}
      </AnalyticsProvider>
    </RecaptchaProvider>
  </LoginPageContext.Provider>
);
